import type { Dispatch, SetStateAction } from "react";

import beakDown2Black from "../../../../assets/img/icons/beak_down_black2.svg";
import beakDownWhite from "../../../../assets/img/icons/beak_down_white.svg";
import beakUpWhite from "../../../../assets/img/icons/beak_up_white.svg";
import noImage from "../../../../assets/img/no_image.jpg";
import bronzeStar from "../../../../assets/img/star_bronze.png";
import goldStar from "../../../../assets/img/star_gold.png";
import silverStar from "../../../../assets/img/star_silver.png";
import { REACT_APP_API_URL } from "../../../../config/config";
import { useSiteData } from "../../../../contexts/SiteDataContext";
import type { RatingCategory, Vpn } from "../../../../types";
import { isOtherCategory, isRuFocused } from "../../../../utils/vpnUtils";

// The star shows the *category*, not `vpn.rating` — that one is a ranking
// weight for weighted_shuffle (VPN_RATING.md), never the star. "Прочие" and
// uncategorised rows deliberately get no star.
const TIER_STARS: Record<string, string> = {
  gold: goldStar,
  silver: silverStar,
  bronze: bronzeStar,
};

interface RatingPageVpnCardHeaderProps {
  vpn: Vpn;
  rating: RatingCategory;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

function RatingPageVpnCardHeader({
  vpn,
  rating,
  isOpen,
  setIsOpen,
}: RatingPageVpnCardHeaderProps) {
  const { siteData } = useSiteData();

  // Read from the VPN, not the section: silver and bronze are rendered as one
  // merged section (mergeGoodTiers), so `rating.tier` would stamp a silver
  // star on every bronze service in it. Falls back to the section for payloads
  // that predate `effective_rating`.
  const vpnTier = vpn.effective_rating?.tier ?? rating.tier;
  const tierStar = vpnTier ? TIER_STARS[vpnTier] : undefined;
  const tierName = vpn.effective_rating?.name ?? rating.name;

  // The badge is a statement about Russia specifically, so it stays off the
  // KZ/AZ sites. An unset region behaves as "ru", matching how this card
  // already falls back for websites and promo codes.
  const regionCode = siteData?.region?.code;
  const showsRuInfo = !regionCode || regionCode === "ru";
  const isRuOriented =
    isRuFocused(vpn.russia_context_awareness) && !isOtherCategory(rating);

  return (
    <div className="flex justify-between items-center md:items-start mb-3 gap-3 border-light md:border-b-0 pb-3 md:pb-0">
      <div className="block lg:hidden lg:flex w-[4.625rem] h-[3.75rem] justify-center items-center">
        <img
          src={vpn.logo ? REACT_APP_API_URL + vpn.logo : noImage}
          onError={(e) => {
            e.currentTarget.src = noImage;
          }}
          alt={vpn.name}
          className="w-full h-full object-contain rounded-xl bg-black-0f"
        />
      </div>

      {/* Mobile stacks the badge under the name; desktop keeps it inline. */}
      <div className="flex-1 flex flex-col md:flex-row md:flex-wrap md:items-center gap-y-1 md:gap-x-3">
        <h3 className="text-lg md:text-2xl font-bold text-left flex items-center gap-1 md:gap-3">
          {tierStar && (
            <img
              src={tierStar}
              alt={tierName}
              className="w-7 h-7 md:w-8 md:h-8 shrink-0"
            />
          )}
          {vpn.name}
        </h3>
        {isRuOriented && showsRuInfo && (
          <span className="badge-ru-focus text-white text-xs font-semibold rounded-full px-3 py-1 leading-normal whitespace-nowrap self-start md:self-auto">
            Ориентирован на РФ
          </span>
        )}
      </div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="self-start"
        data-umami-event="VPN Card Expand"
        data-umami-event-vpn-name={vpn.name}
      >
        <div className="block md:hidden translate-x-3 -translate-y-3 p-3">
          <img
            src={isOpen ? beakUpWhite : beakDownWhite}
            alt="arrow right"
            className="w-[1.5rem] h-auto bg-yellow rounded-full"
          />
        </div>
        <div className="hidden md:flex flex items-center flex-row gap-1 select-none">
          <span className="text-xs font-light underline italic">
            {isOpen ? "Скрыть" : "Подробнее"}
          </span>
          <span className="w-6 h-6 flex items-center justify-center">
            <img
              src={isOpen ? beakDown2Black : beakDown2Black}
              alt="arrow right"
              className={isOpen ? "rotate-180" : ""}
            />
          </span>
        </div>
      </button>
    </div>
  );
}

export default RatingPageVpnCardHeader;
